/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();

        // PDP-1 A/B test. Only product templates render data-ab-mode.
        this.abMode = this.dataset.abMode === 'true';
        if (this.abMode) this.initAbMode();

        // Evaluate the current position once, now. The listener above only
        // fires on the NEXT scroll, so without this the bar stays hidden
        // whenever the page arrives already scrolled past the buy box: a
        // reload or back-navigation (the browser restores scroll silently), an
        // anchor link, or scrolling before this script finished loading.
        StickyAtc.handleScroll.call(this);
        if (document.readyState === 'complete') {
          window.requestAnimationFrame(() => StickyAtc.handleScroll.call(this));
        } else {
          window.addEventListener('load', () => StickyAtc.handleScroll.call(this), { once: true });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.clearTimeout(this.abSyncTimer);
        if (this.abBarClickHandler) this.removeEventListener('click', this.abBarClickHandler);
        if (this.abChangeHandler) this.section.removeEventListener('change', this.abChangeHandler);
        if (this.abOutsideHandler) document.removeEventListener('click', this.abOutsideHandler);
        if (this.abKeyHandler) document.removeEventListener('keydown', this.abKeyHandler);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;

        // AB mode: the bar carries its own size control, so ask for the size
        // here rather than throwing the shopper back up the page - adding
        // without scrolling back is the whole point of the test.
        if (this.abMode && this.sizeList && this.sizeFieldset
            && !this.sizeFieldset.querySelector('input.js-option:checked')) {
          this.toggleAbSizeList(true);
          return;
        }

        // AB mode: hand the add to the buy box's OWN button instead of
        // submitting this bar's form.
        //
        // The copy loop further down only copies fields this form does not
        // already have, and Liquid baked an <input name="id"> into it at page
        // load. On a curated pack that stale id is the SINGLES variant, so the
        // bar added one tee for $29.95 instead of the pack. Clicking the real
        // button runs the pack, bundle and line-item-property logic exactly as
        // the buy box would, so the bar can never diverge from it again.
        if (this.abMode) {
          const mainButton = this.section.querySelector('buy-buttons button[name="add"]');
          if (mainButton && !mainButton.disabled) {
            this.tagAbAdd();
            mainButton.click();
          }
          // Always stop here. Falling through would run the theme's legacy
          // path, which submits THIS form and its Liquid-baked singles variant
          // id - that is how a sold-out pack size once added a plain black tee.
          return;
        }

        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();

        if (this.abMode) this.syncAbDetails(evt.detail.variant);
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }

      /**
       * PDP-1 A/B test (ITAM, Sep 2026).
       * Adds what the mockup shows and the stock bar lacks: a size control, the
       * price, and a tag on adds. Inert unless the snippet rendered data-ab-mode.
       */
      initAbMode() {
        this.sizeWrap = this.querySelector('.sticky-atc__size');
        this.priceEl = this.querySelector('.sticky-atc__details__price');

        // Not scoped to <variant-picker>: the pack templates render their
        // option controls and price map outside that tag entirely.
        const sizeFieldsets = Array.from(this.section.querySelectorAll(
          '.option-selector[data-option="Size"], .option-selector[data-option="Waist"]'
        ));
        this.sizeFieldset = sizeFieldsets.find((fs) =>
          fs.getClientRects().length > 0 && fs.querySelectorAll('input.js-option').length > 1
        ) || sizeFieldsets[0] || null;

        this.priceData = this.readAbPriceData();
        this.abVariants = this.readAbVariants();
        this.abPacks = this.readAbPacks();
        // Liquid rendered the variant title here; keep it as the fallback for
        // products whose option selectors are hidden (a single colour value).
        this.abFallbackTitle = this.variantTitle ? this.variantTitle.textContent.trim() : '';

        if (this.sizeFieldset && this.sizeWrap) this.buildAbSizeControl();

        // Picking a pack or a style makes the theme re-render its pickers, and
        // that happens AFTER this event fires. Sync once immediately so the bar
        // never looks frozen, then again once the re-render has landed -
        // otherwise the line keeps describing the previous pack.
        this.abChangeHandler = () => {
          this.syncAbDetails();
          window.clearTimeout(this.abSyncTimer);
          this.abSyncTimer = window.setTimeout(() => this.syncAbDetails(), 350);
        };
        this.section.addEventListener('change', this.abChangeHandler);

        // Sits directly after the colours, NOT at the end of the row: it is a
        // product attribute like the colours, so it belongs with them rather
        // than stranded past the size control. Outside the ellipsising text,
        // so it still cannot be truncated away.
        this.lengthEl = document.createElement('span');
        this.lengthEl.className = 'sticky-atc__length';
        this.lengthEl.hidden = true;
        if (this.variantTitle) {
          this.variantTitle.after(this.lengthEl);
        } else {
          this.variantContainer.prepend(this.lengthEl);
        }

        // Tapping the bar itself - anywhere but the controls - takes the
        // shopper up to the buy box.
        this.abBarClickHandler = (evt) => {
          if (evt.target.closest('.sticky-atc__buttons, .sticky-atc__size, .sticky-atc__error')) return;
          const target = this.section.querySelector('#product-info') || this.section;
          if (window.theme && typeof theme.scrollToRevealElement === 'function') {
            theme.scrollToRevealElement(target);
          } else {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        };
        this.addEventListener('click', this.abBarClickHandler);

        this.syncAbDetails();
        requestAnimationFrame(() => this.syncAbDetails());
      }

      /**
       * The selected Length (Reg / Tall), read the same way as the other
       * options but rendered separately so it cannot be truncated.
       * @returns {string} the value, or an empty string.
       */
      readAbLengthValue() {
        const fs = Array.from(this.section.querySelectorAll('.option-selector[data-option]'))
          .find((el) => /length/i.test(el.dataset.option || '') && el.getClientRects().length > 0);
        const checked = fs?.querySelector('input.js-option:checked');
        return checked ? checked.value.trim() : '';
      }

      /**
       * Reads the money-formatted price map(s) the picker already prints.
       * Pack templates render more than one, so merge them.
       * @returns {object|null}
       */
      readAbPriceData() {
        const scripts = this.section.querySelectorAll('script[type="application/json"]');
        const merged = {};
        scripts.forEach((script) => {
          try {
            Object.assign(merged, JSON.parse(script.textContent).formatted || {});
          } catch (e) {
            /* slider configs and the like contribute nothing */
          }
        });
        return Object.keys(merged).length ? merged : null;
      }

      /**
       * Builds the size control from the buy box's own fieldset, so sort order,
       * the discontinued hides and availability all carry over.
       *
       * Deliberately NOT a native <select>: its popup is positioned by the OS
       * and lands far from the control when the trigger sits inside a
       * position:fixed bar. This popover is anchored to the bar itself.
       */
      buildAbSizeControl() {
        const radios = Array.from(this.sizeFieldset.querySelectorAll('input.js-option'));
        if (radios.length < 2) {
          this.sizeWrap.hidden = true;
          return;
        }

        this.sizeWrap.textContent = '';

        this.sizeBtn = document.createElement('button');
        this.sizeBtn.type = 'button';
        this.sizeBtn.className = 'sticky-atc__size__btn';
        this.sizeBtn.setAttribute('aria-haspopup', 'listbox');
        this.sizeBtn.setAttribute('aria-expanded', 'false');

        this.sizeLabel = document.createElement('span');
        this.sizeLabel.className = 'sticky-atc__size__label';
        this.sizeLabel.textContent = this.sizeFieldset.dataset.option;

        this.sizeValueEl = document.createElement('span');
        this.sizeValueEl.className = 'sticky-atc__size__value';

        this.sizeBtn.append(this.sizeLabel, this.sizeValueEl);

        this.sizeList = document.createElement('ul');
        this.sizeList.className = 'sticky-atc__size__list';
        this.sizeList.setAttribute('role', 'listbox');
        this.sizeList.hidden = true;

        radios.forEach((radio) => {
          const label = this.sizeFieldset.querySelector(`label[for="${radio.id}"]`);
          if (label && label.classList.contains('hidden')) return;

          const item = document.createElement('li');
          item.className = 'sticky-atc__size__option';
          item.setAttribute('role', 'option');
          item.dataset.value = radio.value;
          item.textContent = (label?.querySelector('.js-value')?.textContent || radio.value).trim();
          this.sizeList.append(item);
        });

        this.sizeWrap.append(this.sizeBtn, this.sizeList);
        this.sizeWrap.hidden = this.sizeList.children.length === 0;

        this.sizeBtn.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.toggleAbSizeList(this.sizeList.hidden);
        });

        this.sizeList.addEventListener('click', (evt) => {
          const item = evt.target.closest('.sticky-atc__size__option');
          if (!item || item.getAttribute('aria-disabled') === 'true') return;
          this.applyAbSize(item.dataset.value);
          this.toggleAbSizeList(false);
        });

        // Close on an outside click or Escape, like any other menu.
        this.abOutsideHandler = (evt) => {
          if (!this.sizeWrap.contains(evt.target)) this.toggleAbSizeList(false);
        };
        this.abKeyHandler = (evt) => {
          if (evt.key === 'Escape') this.toggleAbSizeList(false);
        };
        document.addEventListener('click', this.abOutsideHandler);
        document.addEventListener('keydown', this.abKeyHandler);
      }

      /**
       * Opens or closes the size popover.
       * @param {boolean} open - Whether it should be open.
       */
      toggleAbSizeList(open) {
        if (!this.sizeList) return;

        // Sit the popover clear above the WHOLE bar. It is positioned against
        // the size wrap, which sits on the middle line, so anchoring to that
        // alone would overlap the product title.
        if (open) {
          const barTop = this.getBoundingClientRect().top;
          const wrapBottom = this.sizeWrap.getBoundingClientRect().bottom;
          this.sizeList.style.bottom = `${Math.round(wrapBottom - barTop) + 10}px`;
        }

        this.sizeList.hidden = !open;
        this.sizeBtn.setAttribute('aria-expanded', String(!!open));
        this.sizeWrap.classList.toggle('sticky-atc__size--open', !!open);
      }

      /**
       * Applies a size by driving the buy box's own control, so the picker runs
       * all of its normal work. The bar never writes a variant id itself.
       * @param {string} value - The size value to select.
       */
      applyAbSize(value) {
        const radio = Array.from(this.sizeFieldset.querySelectorAll('input.js-option'))
          .find((r) => r.value === value);
        if (!radio || radio.disabled) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Mirrors the current selection into the bar: colour on the text line,
       * size on the button, price underneath.
       * @param {object} [variant] - Variant from the change event, if any.
       */
      syncAbDetails(variant) {
        const idInput = this.section.querySelector('product-form buy-buttons input[name="id"]');
        const variantId = variant?.id || idInput?.value;
        const current = variant || null;

        this.refreshAbSizeAvailability();

        if (this.variantTitle) {
          let parts = this.readAbOptionValues();

          // A product with one colour renders that selector hidden, so there is
          // nothing to read; fall back to the variant title minus the size.
          if (!parts.length) {
            const sizes = this.sizeFieldset
              ? new Set(Array.from(this.sizeFieldset.querySelectorAll('input.js-option')).map((r) => r.value))
              : new Set();
            parts = String(current?.title ?? this.abFallbackTitle ?? '')
              .split(' / ')
              .map((part) => part.trim())
              .filter((part) => part && !sizes.has(part));
          }

          this.variantTitle.textContent = parts.join(' \u00b7 ');

          // The theme hides this line whenever the section has no
          // <variant-picker> element, which is the case on the pack templates -
          // that is why the colour and the Logo/No Logo choice went missing
          // there. In AB mode the line is driven from the option selectors
          // instead, so show it whenever there is something to say.
          this.variantTitle.toggleAttribute('hidden', parts.length === 0);
          if (parts.length) this.variantContainer.hidden = false;
          this.variantContainer.classList.toggle('sticky-atc__details__variant--bare', parts.length === 0);
        }

        this.updateAbPackImage();

        if (this.lengthEl) {
          const length = this.readAbLengthValue();
          this.lengthEl.textContent = length;
          this.lengthEl.hidden = !length;
        }

        this.updateAbPrice(variantId);

        // Button state. With no size picked the theme would fall back to
        // "View details", which here only means "no size yet", so offer to add.
        // Once a size IS picked, mirror the buy box exactly - otherwise a sold
        // out size still read "Add to cart" on a button that does nothing.
        if (this.submitBtn && this.sizeList) {
          const mainButton = this.section.querySelector('buy-buttons button[name="add"]');
          const sizeChosen = !!this.sizeFieldset?.querySelector('input.js-option:checked');

          if (!sizeChosen && this.submitBtn.dataset.addToCartText) {
            this.submitBtn.textContent = this.submitBtn.dataset.addToCartText;
            this.submitBtn.disabled = false;
          } else if (mainButton) {
            this.submitBtn.textContent = mainButton.textContent.trim();
            this.submitBtn.disabled = mainButton.disabled;
          }
        }
      }

      /**
       * The theme prints its full variant list beside the price map. It is the
       * only reliable source for sold-out sizes: the buy box renders every size
       * enabled and unstyled at load, so there is nothing in the DOM to mirror.
       * @returns {Array} variant objects, or an empty array.
       */
      readAbVariants() {
        const merged = [];
        this.section.querySelectorAll('script[type="application/json"]').forEach((script) => {
          try {
            const parsed = JSON.parse(script.textContent);
            if (Array.isArray(parsed.variants)) merged.push(...parsed.variants);
          } catch (e) {
            /* not a variant map */
          }
        });
        return merged;
      }

      /**
       * The raw selected value of every option except the size, used to pin
       * down which variant a given size would resolve to.
       * @returns {string[]}
       */
      readAbSelectedOptionValues() {
        const sizeName = this.sizeFieldset?.dataset.option;
        const values = [];
        const seen = new Set();
        this.section.querySelectorAll('.option-selector[data-option]').forEach((fs) => {
          const name = fs.dataset.option;
          if (!name || name === sizeName || seen.has(name)) return;
          if (fs.getClientRects().length === 0) return;
          const checked = fs.querySelector('input.js-option:checked');
          if (!checked) return;
          seen.add(name);
          values.push(checked.value.trim());
        });
        return values;
      }

      /**
       * Whether the variant this size would resolve to is sold out.
       * @param {string} sizeValue - The size to test.
       * @returns {boolean}
       */
      abSizeSoldOut(sizeValue) {
        if (!this.abVariants || !this.abVariants.length) return false;

        // A curated pack is a set of single tees, so it is only buyable in a
        // size when EVERY colour in the set is. The pack itself is not in the
        // variant list - that list belongs to the singles product - which is
        // why matching on the pack's combined label ("Black, Forest Green,
        // Cream") found nothing and every size looked available.
        const pack = this.currentAbPack();
        if (pack && Array.isArray(pack.colors)) {
          const length = this.readAbLengthValue();
          return !pack.colors.every(
            (colour) => this.abVariantAvailable([colour.value, sizeValue, length], false)
          );
        }

        const others = this.readAbSelectedOptionValues();
        return !this.abVariantAvailable([...others, sizeValue], true);
      }

      /**
       * The pack definitions the curated-pack picker prints, which name the
       * colours making up each set.
       * @returns {Array|null}
       */
      readAbPacks() {
        for (const script of this.section.querySelectorAll('script[type="application/json"]')) {
          try {
            const parsed = JSON.parse(script.textContent);
            if (Array.isArray(parsed.packs)) return parsed.packs;
          } catch (e) {
            /* not the pack map */
          }
        }
        return null;
      }

      /**
       * The pack the shopper currently has selected, if any.
       * @returns {object|null}
       */
      currentAbPack() {
        if (!this.abPacks) return null;
        const checked = Array.from(this.section.querySelectorAll('input.js-option:checked'))
          .find((input) => input.dataset.packValue);
        if (!checked) return null;
        return this.abPacks.find((p) => String(p.id) === String(checked.dataset.packValue)) || null;
      }

      /**
       * Whether a variant carrying all of these option values is available.
       * @param {Array<string>} required - Option values that must all match.
       * @param {boolean} unknownIsAvailable - What to answer when no variant
       *   matches at all. True on the plain PDP, where a failed match means the
       *   lookup is wrong rather than the size being gone. False for packs,
       *   where a missing colour/size genuinely does not exist.
       * @returns {boolean}
       */
      abVariantAvailable(required, unknownIsAvailable) {
        const wanted = required.filter(Boolean).map((v) => String(v).trim());
        const match = this.abVariants.find((variant) => {
          const opts = (variant.options || []).map((o) => String(o).trim());
          return wanted.every((w) => opts.includes(w));
        });
        if (!match) return unknownIsAvailable;
        return match.available !== false;
      }

      /**
       * Every option the shopper has chosen except the size, which has its own
       * control. Read straight off the buy box's selectors so it works the same
       * on the plain PDP (Colour) and on the pack PDPs (Style, Colour, pack
       * quantity, Length), and so hidden pickers never contribute.
       * @returns {string[]}
       */
      readAbOptionValues() {
        const sizeName = this.sizeFieldset?.dataset.option;
        const parts = [];
        const seen = new Set();

        // The title already carries the product name, so anything it states
        // (the Logo / No Logo style, a colour in the name) is not repeated.
        const title = (this.querySelector('.sticky-atc__details__title')?.textContent || '').toLowerCase();

        this.section.querySelectorAll('.option-selector[data-option]').forEach((fs) => {
          const name = fs.dataset.option;
          if (!name || name === sizeName || seen.has(name)) return;
          if (fs.getClientRects().length === 0) return;

          // The pack count is already implied by the colour list ("3x Black,
          // 3x White"), so it earns no room in a one-line bar.
          if (/quantity/i.test(name)) return;

          // Length (Reg / Tall) is rendered beside the size instead. On a pack
          // the colour list is long enough to ellipsis, and Tall was being cut
          // off - it is fit information, so it must never be the bit that goes.
          if (/length/i.test(name)) return;

          const checked = fs.querySelector('input.js-option:checked');
          const value = checked && checked.value.trim();
          if (!value) return;
          if (title.includes(value.toLowerCase())) return;

          seen.add(name);
          parts.push(value);
        });

        return parts;
      }

      /**
       * On the pack PDPs the media gallery does not follow the pack the shopper
       * picked, so the bar kept showing a single tee beside a three-colour
       * pack. Prefer the image on the selected pack option. Scoped to option
       * names containing "pack" so the plain PDP keeps its gallery shot rather
       * than a small colour swatch.
       * @returns {boolean} whether a pack image was applied.
       */
      updateAbPackImage() {
        if (!this.imageContainer) return false;

        const packFs = Array.from(this.section.querySelectorAll('.option-selector[data-option]'))
          .find((fs) => /pack/i.test(fs.dataset.option || '') && fs.getClientRects().length > 0);
        if (!packFs) return false;

        const checked = packFs.querySelector('input.js-option:checked');
        if (!checked) return false;

        const img = packFs.querySelector(`label[for="${checked.id}"] img`);
        if (!img) return false;

        this.imageContainer.textContent = '';
        this.imageContainer.append(img.cloneNode(true));
        return true;
      }

      /**
       * Re-reads which sizes the buy box allows and mirrors that onto the
       * popover, and shows the chosen size on the button.
       */
      refreshAbSizeAvailability() {
        if (!this.sizeFieldset || !this.sizeList) return;

        const radios = Array.from(this.sizeFieldset.querySelectorAll('input.js-option'));
        const chosen = radios.find((r) => r.checked);

        Array.from(this.sizeList.children).forEach((item) => {
          const radio = radios.find((r) => r.value === item.dataset.value);
          const label = radio && this.sizeFieldset.querySelector(`label[for="${radio.id}"]`);
          const unavailable = !radio || radio.disabled || !!label?.classList.contains('hidden');
          item.setAttribute('aria-disabled', String(unavailable));

          // Sold out is greyed but still selectable, mirroring the PDP, where
          // picking a sold-out size reveals the back in stock form rather than
          // refusing the click.
          const soldOut = !unavailable && this.abSizeSoldOut(item.dataset.value);
          if (soldOut) item.dataset.soldOut = 'true';
          else delete item.dataset.soldOut;
          item.setAttribute('aria-selected', String(!!chosen && chosen.value === item.dataset.value));
        });

        // Until a size is chosen the button reads "Select size", so the bar
        // never implies a size the shopper has not actually picked.
        if (chosen) {
          this.sizeValueEl.textContent = chosen.value;
          this.sizeLabel.hidden = false;
          this.sizeWrap.classList.remove('sticky-atc__size--empty');
        } else {
          this.sizeValueEl.textContent = `Select ${this.sizeFieldset.dataset.option.toLowerCase()}`;
          this.sizeLabel.hidden = true;
          this.sizeWrap.classList.add('sticky-atc__size--empty');
        }
      }

      /**
       * Writes the money-formatted price for a variant into the bar.
       * @param {string|number} variantId - Variant id.
       */
      updateAbPrice(variantId) {
        if (!this.priceEl) return;

        // Prefer whatever the buy box is actually showing. On the pack PDPs the
        // price depends on the pack, not on a single variant, so the per-variant
        // map returns the singles price and the bar disagreed with the page.
        //
        // The exception is a price RANGE, which the theme shows until a size is
        // picked ("$19.95 - $32.97"). That is too noisy for a one-line bar, so
        // use the exact variant price when one resolves and keep the range only
        // as a last resort.
        let record = this.readAbRenderedPrice();
        const isRange = record && /\s[-\u2013]\s/.test(record.price);
        if (!record || isRange) {
          const exact = this.priceData?.[variantId];
          if (exact) record = exact;
        }
        if (!record) {
          const ids = Array.from(this.section.querySelectorAll('input[name="id"]'))
            .map((i) => i.value).filter(Boolean);
          for (const id of ids) {
            if (this.priceData?.[id]) { record = this.priceData[id]; break; }
          }
        }

        this.priceEl.textContent = '';
        if (!record) return;

        this.priceEl.classList.toggle('sticky-atc__details__price--on-sale', !!record.compareAtPrice);
        this.priceEl.append(document.createTextNode(StickyAtc.moneyText(record.price)));
        if (record.compareAtPrice) {
          const was = document.createElement('span');
          was.className = 'sticky-atc__details__price__was';
          was.textContent = StickyAtc.moneyText(record.compareAtPrice);
          this.priceEl.append(was);
        }
      }

      /**
       * Reads the price the buy box is currently rendering. The container holds
       * a block per pricing mode (singles, curated pack, pack) and hides all but
       * the live one, so take the first block that is actually on screen.
       * @returns {{price: string, compareAtPrice: ?string}|null}
       */
      readAbRenderedPrice() {
        const containers = Array.from(this.section.querySelectorAll('.product-info__price'));
        for (const container of containers) {
          if (container.getClientRects().length === 0) continue;

          const block = Array.from(container.querySelectorAll('.price'))
            .find((p) => p.getClientRects().length > 0);
          if (!block) continue;

          const current = block.querySelector('.price__current')?.textContent.trim();
          if (!current) continue;

          const was = block.querySelector('.price__was, .price__compare, s, del')?.textContent.trim();
          return { price: current, compareAtPrice: was || null };
        }
        return null;
      }

      /**
       * The price map stores money as markup, e.g. "<span class=money>$32.95</span>".
       * Parse it off-document and keep only the text.
       * @param {string} value - Money string, possibly wrapped in markup.
       * @returns {string}
       */
      static moneyText(value) {
        if (typeof value !== 'string') return '';
        if (value.indexOf('<') === -1) return value.trim();
        const holder = document.createElement('div');
        holder.innerHTML = value;
        return holder.textContent.trim();
      }

      /**
       * Marks an add as having come from the bar. The add itself is unchanged -
       * same form, same cart request - this only labels it so bar adds can be
       * split from buy box adds in GA.
       * NOTE: a GTM tag has to forward add_to_cart_sticky_bar for it to land.
       */
      tagAbAdd() {
        const variantId = this.form?.querySelector('[name="id"]')?.value;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_cart_sticky_bar',
          add_to_cart_source: 'sticky_bar',
          variant_id: variantId
        });
        this.dispatchEvent(new CustomEvent('on:sticky-atc:add', {
          bubbles: true,
          detail: { source: 'sticky_bar', variantId }
        }));
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
